import { Component, EventEmitter, Output, OnInit, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { PartnersService } from '../../services/partners.service';
import { PartnerGroup } from '../../interfaces/partners.interface';
import { environment } from '../../../environments/environment';

type types = 'CHILDREN' | 'CITIZEN' | 'SENIOR' | 'STUDENT';
type MenuContext = 'offers' | 'partners';

@Component({
  selector: 'app-offer-menu',
  standalone: false,
  templateUrl: './offer-menu.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./offer-menu.component.scss']
})
export class OfferMenuComponent implements OnInit, OnChanges {
  @Output() typeSelected = new EventEmitter<types>();
  @Output() groupSelected = new EventEmitter<number | null>();
  @Input() emitOnStart: boolean = true; // Default to offers for backward compatibility
  @Input() context: MenuContext = 'offers'; // Default to offers for backward compatibility
  @Input() selectedGroupId?: number | null; // Allow parent to control selection

  level1Tabs: Array<types> = ['CHILDREN', 'CITIZEN', 'STUDENT', 'SENIOR'];
  selectedType: types = 'CHILDREN';
  level1Labels: { [key in types]: string } = {
    CHILDREN: 'Dla Rodzin',
    CITIZEN: 'Dla mieszkańców',
    STUDENT: 'Dla Studentów', 
    SENIOR: 'Dla Seniorów'
  };

  groups: PartnerGroup[] = [];
  filteredGroups: PartnerGroup[] = [];
  selectedGroup?: PartnerGroup;
  savedGroupId: number = -1;
  private isInitialLoad: boolean = true;
  private categoryFromUrl?: string;

  imagesUrl = environment.rootUrl;

  constructor(
    private partnersService: PartnersService, 
    private router: Router, 
    private location: Location,
    private route: ActivatedRoute
  ) {
    //take name from url oferty/sport_i_rekreacja or partnerzy/restauracje and save it to categoryFromUrl
    this.categoryFromUrl = this.route.snapshot.paramMap.get('categoryName') || undefined;
  }

  ngOnInit(): void {
    this.loadGroups();
 
    
    // Delay loading to allow parent component to initialize first
    setTimeout(() => {
      if (this.emitOnStart) {
        //this.restoreSelection();
      }
      
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedGroupId'] && this.groups.length > 0 && !this.isInitialLoad) {
      // Update selectedGroup based on input from parent (only after initial load)
      if (this.selectedGroupId && this.groups.length > 0) {
        this.selectedGroup = this.groups.find(g => g.id === this.selectedGroupId);
         this.selectedGroupId = this.selectedGroup?.id;
      } else {
        this.selectedGroup = undefined;
        this.selectedGroupId = -1;
      }
    }
  }


  selectGroup(group: PartnerGroup) {
    // If clicking on the same group, unselect it
    if (this.selectedGroup && this.selectedGroup.id === group.id) {
      this.selectedGroup = undefined;
      this.clearSelection();
      this.savedGroupId = 0;
      this.groupSelected.emit(0);
      // Navigate back to base page based on context
      const basePage = this.context === 'partners' ? '/partnerzy' : '/oferty';
      //this.router.navigate([basePage]);
    } else {
      // Select the new group
      this.savedGroupId = group.id;
      this.selectedGroup = group;
      this.saveSelection();
      this.groupSelected.emit(group.id);
      
      // Create URL-friendly group name with proper Polish character handling
      const urlName = group.title
        ? group.title.toLowerCase()
            .replace(/ą/g, 'a')
            .replace(/ć/g, 'c')
            .replace(/ę/g, 'e')
            .replace(/ł/g, 'l')
            .replace(/ń/g, 'n')
            .replace(/ó/g, 'o')
            .replace(/ś/g, 's')
            .replace(/ź/g, 'z')
            .replace(/ż/g, 'z')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
        : 'kategoria';
      
      // Update URL without navigation
      const basePage = this.context === 'partners' ? '/partnerzy' : '/oferty';
      this.location.replaceState(`${basePage}/${urlName}`);
      
    }
  }

  saveSelection() {
    const storageKey = `${this.context}MenuGroupId`;
    localStorage.setItem(storageKey, this.selectedGroup ? String(this.selectedGroup.id) : '');
    this.savedGroupId = this.selectedGroup ? this.selectedGroup.id : -1;
  }

  clearSelection() {
    const storageKey = `${this.context}MenuGroupId`;
    localStorage.removeItem(storageKey);
    this.savedGroupId = 0;
  }

  restoreSelection() {
    const storageKey = `${this.context}MenuGroupId`;
    this.savedGroupId = Number(localStorage.getItem(storageKey)) || -1;
  }

  loadGroups() {
    this.partnersService.getPartnerGroup().subscribe({
      next: (groups) => {
        this.groups = groups;
        
        // Priority 1: Check if there's a category in the URL
        if (this.categoryFromUrl) {
          const matchingGroup = this.findGroupByUrlName(this.categoryFromUrl, groups);

          if (matchingGroup) {
            this.selectedGroup = matchingGroup;
            this.savedGroupId = matchingGroup.id;
            this.saveSelection();
            setTimeout(() => {
              if (this.emitOnStart) {
                this.groupSelected.emit(matchingGroup.id);
              }
            }, 0);
            this.isInitialLoad = false;
            return;
          }
        }
        
        // Priority 2: Check localStorage
        if (this.savedGroupId > 0 && groups.filter(g => g.id === this.savedGroupId).length > 0) {
          this.selectedGroup = groups.find(g => g.id === this.savedGroupId);
          setTimeout(() => {
            if (this.emitOnStart) {
              this.groupSelected.emit(this.savedGroupId);
            }
          }, 0);
        } else if (this.savedGroupId == -1) {
          // Priority 3: Default to first group
          setTimeout(() => {
            if (this.emitOnStart) {
              //this.groupSelected.emit(this.groups[0]?.id);
              this.groupSelected.emit(0);
            }
          }, 0);
        } else {
          setTimeout(() => {
            if (this.emitOnStart) {
              this.groupSelected.emit(this.savedGroupId);
            }
          }, 0);
        }

        this.isInitialLoad = false;
      },
      error: () => {
        this.groups = [];
        this.filteredGroups = [];
        this.selectedGroup = undefined;
        this.isInitialLoad = false;
      }
    });
  }

  private findGroupByUrlName(categoryName: string, groups: PartnerGroup[]): PartnerGroup | undefined {
    // Convert URL-friendly name back to match group titles
    const searchName = categoryName.replace(/-/g, ' ').toLowerCase();
    
    // Helper function to normalize text for comparison (handles Polish characters)
    const normalizeText = (text: string): string => {
      return text.toLowerCase()
        .replace(/ą/g, 'a')
        .replace(/ć/g, 'c')
        .replace(/ę/g, 'e')
        .replace(/ł/g, 'l')
        .replace(/ń/g, 'n')
        .replace(/ó/g, 'o')
        .replace(/ś/g, 's')
        .replace(/ź/g, 'z')
        .replace(/ż/g, 'z')
        .replace(/ /g, '_')
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };
    
    const normalizedSearchName = normalizeText(searchName);
    
    // Find group by matching title (case-insensitive with Polish character normalization)
    const matchingGroup = groups.find(group => {
      const normalizedGroupTitle = normalizeText(group.title);
      return normalizedGroupTitle === normalizedSearchName ||
             group.title.toLowerCase() === searchName;
    });
    
    return matchingGroup;
  }

}